import {
	BankRedeemRequest,
	ConfirmExcusedAbsenceRequest,
	ConfirmExcusedAbsenceResponse,
	UpdateEnrollmentPayerPlanRequest,
	UDTResultWithCupsAnalytes,
	UDTResultEditWithCupsAnalytes,
	CreatePayerPlanScheduleRequest,
	UpdatePayerPlanScheduleRequest,
	PayerPlanSchedule,
} from "@/domain/enrollment.interface";
import {
	FishbowlDrawCoordinates,
	FishbowlDrawResponse,
	FishbowlVisitDraw,
} from "@/domain/reward.interface";
// SIMS-6472: no `axios` (backend-1) import remains — every call in this service now goes
// to backend-2. The last three v1 calls (engine, balances, update-udts) were deleted with
// the single-treatment paths that reached them.
import { instanceBo } from "../utils/axios.create";
// SIMS-8923: the bare axios library, deliberately NOT the configured instances. A presigned
// S3 PUT must go out with no Authorization header and no baseURL — the URL carries its own
// credentials in the query string, and S3 rejects the request if ours are attached too.
import rawAxios from "axios";

const postBankRedeem = async (
	data: BankRedeemRequest,
	beneficiaryId: string,
	enrollmentId: string,
): Promise<{ amountEarned: number; messageEngine: string; link: string }> => {
	return instanceBo
		.post(
			`enrollments/${enrollmentId}/beneficiaries/${beneficiaryId}/bank-redeem`,
			data,
		)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data));
};

const patchUdtResult = async (
	enrollmentId: string,
	udtResultId: string | null,
	data: ConfirmExcusedAbsenceRequest,
): Promise<ConfirmExcusedAbsenceResponse> => {
	// SIMS-6797: migrated to backend-2 (`instanceBo`). Same path and payload; backend-2
	// serves it as a PATCH alongside the PUT on the same path, which is the edit-a-result
	// flow and re-prices the visit. This one deliberately does not.
	return instanceBo
		.patch(`/enrollments/${enrollmentId}/udt-results/${udtResultId}`, data)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const postSendCertificate = async (
	enrollmentId: string,
	payload: {
		providerId: string;
		fileType: string;
		certificateFile: string;
		fileName: string;
	},
): Promise<{ message: string }> => {
	// SIMS-6832: migrated to backend-2 (`instanceBo`). Same path, same payload, same
	// `{ message }` body — backend-2 wraps it in BaseResponse, which `res.data.body`
	// already unwraps exactly as it did for backend-1.
	return instanceBo
		.post(`enrollments/${enrollmentId}/send-certificate`, payload)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err.message));
};

const patchCertificate = async (
	enrollmentId: string,
): Promise<{ message: string }> => {
	// SIMS-6832: migrated to backend-2 (`instanceBo`).
	return instanceBo
		.patch(`enrollments/${enrollmentId}/certificate`, {
			certificateAvailable: false,
		})
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

const updatePayerPlan = async (
	enrollmentId: string,
	data: UpdateEnrollmentPayerPlanRequest,
): Promise<{ message: string }> => {
	return instanceBo
		.patch(`enrollments/${enrollmentId}/payer-plan`, data)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response.data));
};

/**
 * Post UDT Result with Cups and Analytes using backend-2 (instanceBo)
 * Used when event has cupsAndAnalytes flag enabled
 */
const postUdtResultWithCupsAnalytes = async (
	enrollmentId: string,
	beneficiaryId: string,
	data: UDTResultWithCupsAnalytes,
): Promise<{
	amountEarned: number;
	visitNumber: number;
	result: string;
	enrollmentId: string;
	beneficiaryId: string;
	udtResultId: string;
	messageEngine?: string;
	link?: string;
}> => {
	// Strip frontend-only properties from cupsAndAnalytes before sending to backend
	const payload = { ...data };
	if (payload.cupsAndAnalytes) {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { selectedPrimaryAnalytes, selectedSecondaryAnalytes, ...rest } =
			payload.cupsAndAnalytes;
		payload.cupsAndAnalytes = rest;
	}
	return instanceBo
		.post(
			`enrollments/${enrollmentId}/beneficiaries/${beneficiaryId}/balances`,
			payload,
		)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

/**
 * Update UDT Result with Cups and Analytes using backend-2 (instanceBo)
 * Used when event has cupsAndAnalytes flag enabled
 */
const putUdtResultWithCupsAnalytes = async (
	enrollmentId: string,
	udtResultId: string,
	data: UDTResultEditWithCupsAnalytes,
): Promise<{
	amountEarned: number;
	visitNumber: number;
	result: string;
	enrollmentId: string;
	udtResultId: string;
	messageEngine?: string;
	link?: string;
}> => {
	// Strip frontend-only properties from cupsAndAnalytes before sending to backend
	const payload = { ...data };
	if (payload.cupsAndAnalytes) {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { selectedPrimaryAnalytes, selectedSecondaryAnalytes, ...rest } =
			payload.cupsAndAnalytes;
		payload.cupsAndAnalytes = rest;
	}
	return instanceBo
		.put(`enrollments/${enrollmentId}/udt-results/${udtResultId}`, payload)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

export interface BeneficiaryEnrollmentItem {
	enrollmentId: string;
	status: string;
	programId: string;
	programName: string;
	providerId: string;
	providerName: string;
	providerSiteId: string;
	providerSiteName: string;
	startDate: string;
	endDate: string | null;
	expectedEndDate: string | null;
	totalAmountEarned: number;
	bankBalance: number;
	orgUnitId?: string;
	additionalStaffIds?: string[];
	smartProgramFields?: Record<string, unknown>;
	treatmentPlanId?: string;
	eligibilityDate?: string | null;
	programType?: string;
	programCmRoles?: string[];
	programAdditionalStaffRoles?: string[];
	programEligibility?: boolean;
	programConsent?: boolean;
	programIncentiveOnDemand?: boolean;
	programOnDemandIncentiveMin?: number;
	programOnDemandIncentiveMax?: number;
	programDurationInWeeks?: number;
	invitationSentAt?: string | null;
	invitationStatus?: "sent" | "opened" | "first_login" | null;
	// Pause/Resume (SIMS-4451 / SIMS-4459) — fields may not be populated by
	// older backend revisions. Treat absent as falsy.
	allowEnrollmentPause?: boolean;
	disableEnrollmentPause?: boolean;
	pausedAt?: string | null;
	pauseReason?: PauseReason | null;
	customPauseReason?: string | null;
	expectedResumeDate?: string | null;
}

export type PauseReason =
	| "MEDICAL_LEAVE"
	| "PERSONAL_EMERGENCY"
	| "HOSPITALIZATION"
	| "OTHER";

export type ResumeOption = "CONTINUE" | "SHIFT_FORWARD" | "RESTART";

export interface PauseEnrollmentPayload {
	reason: PauseReason;
	customReason?: string;
	expectedResumeDate?: string;
}

export interface ResumeEnrollmentPayload {
	option: ResumeOption;
	daysToShift?: number;
}

const createOnDemandIncentive = async (
	enrollmentId: string,
	amount: number,
	createdBy: string,
): Promise<{ balance: number; totalAmountEarned: number }> => {
	return instanceBo
		.post(`enrollments/${enrollmentId}/on-demand-incentive`, {
			amount,
			createdBy,
		})
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

const reconfirmEligibility = async (
	enrollmentId: string,
	confirmedBy: string,
): Promise<unknown> => {
	return instanceBo
		.patch(`enrollments/${enrollmentId}/eligibility`, { confirmedBy })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

const pauseEnrollment = async (
	enrollmentId: string,
	payload: PauseEnrollmentPayload,
	userId: string,
): Promise<{ enrollmentId: string; status: string }> => {
	return instanceBo
		.post(`enrollments/${enrollmentId}/pause?userId=${userId}`, payload)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

const resumeEnrollment = async (
	enrollmentId: string,
	payload: ResumeEnrollmentPayload,
	userId: string,
): Promise<{ enrollmentId: string; status: string }> => {
	return instanceBo
		.post(`enrollments/${enrollmentId}/resume?userId=${userId}`, payload)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

export interface PauseHistoryItem {
	pausedAt: string;
	pausedBy: string;
	pausedByName?: string;
	reason: PauseReason;
	customReason?: string;
	expectedResumeDate?: string;
	pausedDayNumber: number;
	resumedAt?: string;
	resumedBy?: string;
	resumedByName?: string;
	resumeOption?: ResumeOption;
	daysShifted?: number;
}

const getPauseHistory = async (
	enrollmentId: string,
): Promise<PauseHistoryItem[]> => {
	return instanceBo
		.get(`enrollments/${enrollmentId}/pause-history`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

// SIMS-4521 / SIMS-4529 — Daily Journey + Current Day status table
export interface DailyJourneyActivity {
	instanceId: string;
	eventId: string;
	eventName?: string;
	validationType?: string;
	status?: string;
	plannedFor?: string;
	notifiedAt?: string;
	submittedAt?: string;
	submittedData?: Record<string, unknown>;
	amountEarned?: number;
	result?: string;

	/**
	 * The day's recorded TIR assessment — SIMS-8261, surfaced by SIMS-8514.
	 *
	 * 🔴 Its ABSENCE on an activity that IS here means the plan asked and the
	 * assessment produced nothing auditable yet: AC4's "Pending". A day the plan
	 * never asked about has no activity at all (AC5). A day that ran and could
	 * not be assessed HAS a record, with `coverageMet: false` (AC3).
	 */
	tirAssessment?: {
		percentage: number;
		readingsExpected: number;
		readingsPresent: number;
		coveragePercentage: number;
		coverageMet: boolean;
		coverageThreshold: number;
		targetRangeLow: number;
		targetRangeHigh: number;
	};

	/** The event's own configured result list — SIMS-8514 AC6/AC8. */
	eventResults?: Array<{
		id?: string;
		display?: string;
		shortName?: string;
		displayColor?: string;
		validResult?: boolean;
	}>;
}

export interface DailyJourneyDay {
	date: string; // YYYY-MM-DD in participant tz
	dayNumber: number | null;
	activities: DailyJourneyActivity[];
	completedCount: number;
	totalCount: number;
}

export interface DailyJourneyResponse {
	enrollmentId: string;
	enrollmentStatus: string;
	isPaused: boolean;
	programName?: string;
	currentDay?: number;
	programDurationDays?: number | null;
	enrollmentDate?: string | null;
	cmStartDate?: string | null;
	ianaTimeZone: string;
	today: DailyJourneyDay;
	history: DailyJourneyDay[];
	/** SIMS-8506 AC6. Absent on responses from a backend that predates it. */
	historyRules?: HistoryRuleState[];
}

/**
 * What is driving a history-dependent plan rule — SIMS-8506 AC6.
 *
 * Every number is derived server-side from recorded history at request time,
 * so it already reflects a day corrected a moment ago. Nothing here is a stored
 * counter, and the screen must not compute its own.
 */
export interface HistoryRuleState {
	ruleId: string;
	label?: string;
	/** Consecutive missed days as of today, by this rule's unassessed policy. */
	consecutiveMissedDays: number;
	/** How many consecutive missed days trigger the rule. */
	triggersAt: number;
	activationsUsed: number;
	/** Absent means uncapped. */
	maxActivations?: number;
	active: boolean;
	inactiveBecause?: "RUN_TOO_SHORT" | "CAP_REACHED" | "AWAITING_RESULT";
}

const getDailyJourney = async (
	enrollmentId: string,
): Promise<DailyJourneyResponse> => {
	return instanceBo
		.get(`enrollments/${enrollmentId}/daily-journey`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

// SIMS-4545 — Participant Input (UDTResults + journals)
export type ParticipantInputFilter = "manual" | "automated" | "all";

export interface ParticipantInputActivityItem {
	type: "ACTIVITY";
	timestamp: string;
	activity: DailyJourneyActivity & { entryMethod?: string };
	/**
	 * The threshold THIS entry's own day was judged against — SIMS-8523 AC3.
	 *
	 * Resolved server-side per entry from the day the activity was planned for,
	 * never from the plan's current day: a plan can tighten its target
	 * mid-course, and a historical row rendered against today's threshold
	 * silently re-scores the past.
	 *
	 * Absent where the plan tests no threshold, and absent where the day
	 * measured more than one thing and no single target can honestly be named.
	 * Both are real answers and neither may be replaced with a default.
	 */
	dayTarget?: number;
}

export interface ParticipantInputJournalItem {
	type: "JOURNAL";
	timestamp: string;
	journal: {
		journalId: string;
		content: string;
		contentPreview: string;
		mood?: string;
		createdAt: string;
	};
}

export type ParticipantInputItem =
	| ParticipantInputActivityItem
	| ParticipantInputJournalItem;

export interface ParticipantInputResponse {
	items: ParticipantInputItem[];
	hasJournalFeature: boolean;
	filter: ParticipantInputFilter;
	total: number;
}

const getParticipantInput = async (
	enrollmentId: string,
	filter: ParticipantInputFilter = "all",
): Promise<ParticipantInputResponse> => {
	return instanceBo
		.get(`enrollments/${enrollmentId}/participant-input?filter=${filter}`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

const getBeneficiaryEnrollments = async (
	beneficiaryId: string,
	userId?: string,
	notEnrolled?: boolean,
): Promise<BeneficiaryEnrollmentItem[]> => {
	const params = new URLSearchParams();
	if (userId) params.append("userId", userId);
	if (notEnrolled) params.append("notEnrolled", "true");
	const query = params.toString();
	return instanceBo
		.get(
			`enrollments/beneficiaries/${beneficiaryId}${query ? `?${query}` : ""}`,
		)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

export interface UpdateEnrollmentPayload {
	orgUnitId?: string;
	providerId?: string;
	providerSite?: string;
	additionalStaffIds?: string[];
	smartProgramFields?: Record<string, unknown>;
	updatedBy?: string;
}

export interface CreateEnrollmentPayload {
	beneficiaryId: string;
	programId: string;
	orgUnitId: string;
	providerId: string;
	providerSite: string;
	userId: string;
	medicaidEligibility?: boolean;
	cmEligibility?: boolean;
	treatmentPlan?: string;
	treatmentConfigTemplate?: string;
	treatmentConfig?: {
		behavior?: string;
		duration?: string;
		minHoursBetweenVisits?: string;
		visitsPerPeriod?: string;
		name?: string;
		default?: boolean;
		segment?: number;
		range?: string;
		weeklyVisitsPerPeriod?: string;
		rewardEngineId?: string;
		periodLength?: number;
		cmVisitScheduleOptionIds?: string;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		cmVisitSchedule?: any;
		allowCustomProviderSite?: boolean;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		additionalTreatmentConfigs?: any[];
	};
	smartProgramFields?: Record<string, unknown>;
	additionalStaffIds?: string[];
	// SIMS-8923: `consentKey` names an object already uploaded to S3 by uploadConsentFile and
	// is what the app sends. `consentFile` (base64 in the body) is kept declared because the
	// backend still accepts it — but it was missing here while Enroll Participant already sent
	// a key, which type-checked only because that modal builds its consent payload as `{}`.
	consentKey?: string;
	consentFile?: string;
	fileType?: string;
	fileName?: string;
}

const updateEnrollment = async (
	enrollmentId: string,
	data: UpdateEnrollmentPayload,
): Promise<{
	enrollment: unknown;
	changes: { field: string; oldValue: string; newValue: string }[];
}> => {
	return instanceBo
		.patch(`enrollments/${enrollmentId}`, data)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

const createEnrollment = async (
	data: CreateEnrollmentPayload,
): Promise<{
	beneficiaryId: string;
	enrollmentId: string;
	cmStartDate?: string;
	budgetWarning?: boolean;
}> => {
	return instanceBo
		.post("enrollments", data)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

/**
 * Refuse any presigned upload destination that is not S3 over HTTPS.
 *
 * Throws rather than returning a boolean: the only caller is about to transmit a signed
 * consent form, and there is no useful "continue anyway" branch.
 */
const assertS3UploadUrl = (uploadUrl: string): void => {
	let parsed: URL;
	try {
		parsed = new URL(uploadUrl);
	} catch {
		throw new Error("Consent upload destination is not a valid URL");
	}
	const isS3Host =
		parsed.hostname === "s3.amazonaws.com" ||
		parsed.hostname.endsWith(".amazonaws.com");
	if (parsed.protocol !== "https:" || !isS3Host) {
		throw new Error("Consent upload destination is not an allowed S3 endpoint");
	}
};

/**
 * SIMS-8923 — upload a signed consent form straight to S3 and return its object key.
 *
 * The consent used to be base64-encoded into the enrollment request body. That capped the
 * file at what the API could accept and put a signed PHI document on the API request path.
 * Now the browser asks for a short-lived presigned PUT, uploads to S3 itself, and the
 * enrollment payload carries only `consentKey`.
 *
 * The plain `axios` global is used for the PUT on purpose: `instanceBo` attaches the
 * Authorization header and the account-scoped baseURL, and sending either to S3 would make
 * it reject the request — a presigned URL carries its own credentials in the query string.
 */
const uploadConsentFile = async (file: File): Promise<string> => {
	const { uploadUrl, key } = await instanceBo
		.post("enrollments/consent/presigned-upload-url", {
			contentType: file.type,
			// SIMS-8923: the backend signs this length into the URL, so the slot accepts a
			// body of exactly this size and nothing larger. Sending the real size is what
			// makes the server-side cap enforceable rather than advisory — the browser sets
			// the matching Content-Length itself for a File body.
			contentLength: file.size,
		})
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));

	// The destination is checked before the document is sent, not because the URL comes
	// from an untrusted place — it comes from our own authenticated API — but because what
	// is about to be PUT is a signed PHI document. If a tampered or misconfigured response
	// ever named another host, this refuses rather than uploading a consent form to it.
	// Aikido flags this call as AIK_js_ssrf; the check is a real destination control, and
	// the finding is a by-product of it being satisfied.
	assertS3UploadUrl(uploadUrl);

	await rawAxios.put(uploadUrl, file, {
		headers: { "Content-Type": file.type },
		// Do not let a global interceptor or auth header leak into the S3 request.
		transformRequest: [(data) => data],
	});

	return key;
};

const resendEnrollmentInvitation = async (
	enrollmentId: string,
): Promise<{ message: string }> => {
	return instanceBo
		.post(`enrollments/${enrollmentId}/resend-invitation`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

// ─── Payer Plan Schedule CRUD ────────────────────────────────────

const getPayerPlanSchedule = async (
	enrollmentId: string,
): Promise<PayerPlanSchedule[]> => {
	return instanceBo
		.get(`enrollments/${enrollmentId}/payer-plan-schedule`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

const createPayerPlanSchedule = async (
	enrollmentId: string,
	data: CreatePayerPlanScheduleRequest,
): Promise<unknown> => {
	return instanceBo
		.post(`enrollments/${enrollmentId}/payer-plan-schedule`, data)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

const updatePayerPlanSchedule = async (
	enrollmentId: string,
	entryId: string,
	data: UpdatePayerPlanScheduleRequest,
): Promise<unknown> => {
	return instanceBo
		.patch(`enrollments/${enrollmentId}/payer-plan-schedule/${entryId}`, data)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

const deletePayerPlanSchedule = async (
	enrollmentId: string,
	entryId: string,
): Promise<unknown> => {
	return instanceBo
		.delete(`enrollments/${enrollmentId}/payer-plan-schedule/${entryId}`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

const resolvePayerPlanSchedule = async (
	enrollmentId: string,
	date: string,
): Promise<unknown> => {
	return instanceBo
		.get(`enrollments/${enrollmentId}/payer-plan-schedule/resolve?date=${date}`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

// ─── Call history — Clinical Information tab (SIMS-4561) ─────────
export interface CallRequestItem {
	callId: string;
	participantRequestedAt: string;
}

export interface CallRequestsResponse {
	total: number;
	pageNumber: number;
	pageSize: number;
	data: CallRequestItem[];
}

// pageNumber is 0-based in the UI (MUI TablePagination); the backoffice API is
// 1-based, so we send pageNumber + 1 (matching beneficiary.findIncentiveHistory).
const getCallRequests = async (
	enrollmentId: string,
	pageNumber: number,
	pageSize: number,
): Promise<CallRequestsResponse> => {
	return instanceBo
		.get(
			`enrollments/${enrollmentId}/call-requests?pageNumber=${pageNumber + 1}&pageSize=${pageSize}`,
		)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data || err));
};

/**
 * SIMS-9073 (NFR-1, portal Q-17) — ask the SERVER to resolve one fishbowl draw.
 *
 * Replaces `performSingleDraw`, which rolled `Math.random()` in the browser and posted the
 * total. The request carries only the COORDINATES of the draw; it has no amount, message or
 * probability field, so there is nowhere for a tampered call to name its own prize.
 *
 * Idempotent per coordinate: asking twice for draw 1 returns the draw already resolved, which
 * is what lets a reopened visit show what it already drew instead of drawing again (AC1, AC4).
 */
const resolveFishbowlDraw = async (
	enrollmentId: string,
	coords: FishbowlDrawCoordinates,
): Promise<FishbowlDrawResponse> => {
	return instanceBo
		.post(`enrollments/${enrollmentId}/fishbowl-draws`, coords)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data));
};

/**
 * SIMS-9086 AC1 (NFR-2; SIMS-9068 D-103) — the draws already played for one visit, in draw order.
 *
 * Read-only: the claw game resumes a reopened visit from this (it shows these and offers only the
 * remaining draws). resolveFishbowlDraw cannot be used to look, because asking it for a draw that
 * does not exist yet resolves one.
 */
const listFishbowlDraws = async (
	enrollmentId: string,
	visit: Omit<FishbowlDrawCoordinates, "drawIndex">,
): Promise<FishbowlVisitDraw[]> => {
	return instanceBo
		.get(`enrollments/${enrollmentId}/fishbowl-draws`, {
			params: {
				week: visit.week,
				visitNumber: visit.visitNumber,
				treatmentConfigName: visit.treatmentConfigName,
				generation: visit.generation,
			},
		})
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data));
};

export const enrollmentService = {
	postBankRedeem,
	patchUdtResult,
	postSendCertificate,
	patchCertificate,
	updatePayerPlan,
	// Cups and Analytes (backend-2)
	postUdtResultWithCupsAnalytes,
	putUdtResultWithCupsAnalytes,
	// Enrollment update (backend-2)
	updateEnrollment,
	// Enrollment creation (backend-2)
	createEnrollment,
	// Beneficiary enrollments (backend-2)
	getBeneficiaryEnrollments,
	// Reconfirm eligibility (backend-2)
	reconfirmEligibility,
	// Pause/Resume enrollment (SIMS-4451 / SIMS-4459)
	pauseEnrollment,
	resumeEnrollment,
	// Pause history (SIMS-4483 Tier 1)
	getPauseHistory,
	// Daily Journey (SIMS-4521 / SIMS-4529)
	getDailyJourney,
	// Participant Input — Clinical Information tab (SIMS-4545)
	getParticipantInput,
	// Call history — Clinical Information tab (SIMS-4561)
	getCallRequests,
	// On demand incentive (backend-2)
	createOnDemandIncentive,
	// Resend mobile app invitation (backend-2)
	resendEnrollmentInvitation,
	// Presigned consent upload (SIMS-8923)
	uploadConsentFile,
	// Payer Plan Schedule CRUD (backend-2)
	getPayerPlanSchedule,
	createPayerPlanSchedule,
	updatePayerPlanSchedule,
	deletePayerPlanSchedule,
	resolvePayerPlanSchedule,
	// Server-side fishbowl draw resolution (SIMS-9073)
	resolveFishbowlDraw,
	// A visit's played draws, for resuming the claw game (SIMS-9086)
	listFishbowlDraws,
};
